import { useState, useEffect } from 'react';
import { caseService } from '../../services/api';
import { toast } from 'sonner';
import { isAuthenticationError } from '../../utils/caseSaveErrors';
import { hasActiveSession, SESSION_ENDED_MESSAGE } from '../../utils/session';

export interface CaseSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectCase: (caseId: string, caseTitle: string) => void;
    onCreateNewCase?: () => void;
}

export function CaseSelectionModal({ isOpen, onClose, onSelectCase, onCreateNewCase }: CaseSelectionModalProps) {
    const [cases, setCases] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadCases();
        }
    }, [isOpen]);

    const loadCases = async () => {
        setLoading(true);
        setLoadError(null);
        if (!hasActiveSession()) {
            setCases([]);
            setLoadError(SESSION_ENDED_MESSAGE);
            setLoading(false);
            return;
        }
        try {
            const data = await caseService.getAll();
            setCases(data);
        } catch (e) {
            console.error("Failed to load cases", e);
            // A list left over from an earlier load is not selectable — saving
            // into it fails the same way the refresh just did. Drop it and say
            // what went wrong instead of offering dead rows.
            setCases([]);
            // The global API interceptor redirects expired sessions to login.
            // Avoid flashing a second, misleading case-list error first.
            if (isAuthenticationError(e)) {
                setLoadError(SESSION_ENDED_MESSAGE);
            } else {
                setLoadError("Failed to load cases.");
                toast.error("Failed to load cases.");
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const filteredCases = cases.filter(c => c.title.toLowerCase().includes(search.toLowerCase()) || (c.fir_number && c.fir_number.toLowerCase().includes(search.toLowerCase())));

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-xl flex flex-col max-h-[80vh] border border-border bg-card p-5 shadow-overlay">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Select Existing Case</h3>
                    {onCreateNewCase && (
                        <button
                            onClick={onCreateNewCase}
                            className="px-3 py-1.5 text-xs font-medium bg-primary hover:bg-brand-gold-hover text-primary-foreground text-foreground rounded-md transition-colors"
                        >
                            Create New Case
                        </button>
                    )}
                </div>

                <input
                    type="text"
                    placeholder="Search by case title or FIR number..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md mb-4"
                />

                <div className="flex-1 overflow-y-auto min-h-0 space-y-2 mb-4 pr-2">
                    {loading ? (
                        <div className="text-sm text-muted-foreground py-4 text-center">Loading cases...</div>
                    ) : loadError ? (
                        <div className="py-4 text-center space-y-3" data-testid="case-list-error">
                            <div className="text-sm text-destructive">{loadError}</div>
                            <button
                                onClick={loadCases}
                                className="px-3 py-1.5 text-xs font-medium border border-border rounded-md hover:bg-muted"
                            >
                                Retry
                            </button>
                        </div>
                    ) : filteredCases.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4 text-center">No matching cases found.</div>
                    ) : (
                        filteredCases.map((c) => (
                            <div
                                key={c.id}
                                onClick={() => onSelectCase(c.id, c.title)}
                                className="p-3 border border-border rounded-md hover:border-primary hover:bg-primary/10 cursor-pointer transition-colors"
                            >
                                <div className="font-medium text-sm text-foreground">{c.title}</div>
                                {c.fir_number && (
                                    <div className="text-xs text-muted-foreground mt-1 text-muted-foreground">FIR: {c.fir_number}</div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                <div className="flex justify-end pt-2 border-t border-border mt-auto">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
